import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { type AppDispatch, type RootState } from '../store';
import { KeepAliveProvider } from './RouteStack';
import { LogoTextBrandFontLoadingIndicator } from '../components/common/loadingIndicators/LoadingIndicator';
import { setRedirectTo, clearRedirectTo } from '../store/slices/auth/authSlice';
import RootScreen from '../feature/root/RootScreen';
import { LoginPage } from '../feature/login/LoginPage';
import { MainRouteContainer } from './MainRouteContainer';
import { DetailsRouteContainer } from './DetailsRouteContainer';
import { PublicRouteContainer } from './PublicRouteContainer';
import { ProtectedRoutesWrapper } from './ProtectedRoutesWrapper';
import { MAIN_NAVIGATION_ROUTES, DETAIL_ROUTES, PUBLIC_ROUTES, isPublicRoutePath } from './routes.config';
import { AppInitializer } from '../components/app/AppInitializer';
import { RemoteConfigListener } from '../components/app/RemoteConfigListener';
import { AuthUserListener } from '../components/app/AuthUserListener';
import { PremiumPackagesListener } from '../components/app/PremiumPackagesListener';
import { ExclusiveContentListener } from '../components/app/ExclusiveContentListener';
import { WalletListener } from '../components/app/WalletListener';
import { PaymentExchangeRateLoader } from '../components/app/PaymentExchangeRateLoader';
import { UserDeviceListener } from '../components/app/UserDeviceListener';
import { LastSeenTimestampListener } from '../components/app/LastSeenTimestampListener';
import { ExpauseSheetProvider } from '../components/common/sheet/service/sheetImpl/ExpauseSheet.index';
import { LocaleProvider } from '../contexts/LocaleContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { VideoPlayerProvider } from '../contexts/VideoPlayerContext';
import { IOSCompatibilityCheck } from '../components/common/IOSCompatibilityCheck';
import { AppToaster } from '../components/common/Toaster';
import { VideoUploadProgressOverlay } from '../feature/publish/components/VideoUploadProgressOverlay';
import { AppOverlayHost } from '../components/app/overlays/AppOverlayHost';

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isLoading, isInitializing } = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();

    // Public routes (e.g., the `/legal/*` documents) are mounted under the
    // same parent subtree so they share the protected `KeepAliveOutlet`
    // cache — but they must remain reachable without authentication. Skip
    // both the redirect-to-login and the redirect-store side-effect for
    // those paths.
    const isPublicPath = isPublicRoutePath(location.pathname);

    // Store redirect destination when user is not authenticated
    useEffect(() => {
        if (!isLoading && !isAuthenticated && !isPublicPath) {
            const redirectTo = location.pathname + location.search;
            dispatch(setRedirectTo(redirectTo));
        }
    }, [isAuthenticated, isLoading, isPublicPath, location.pathname, location.search, dispatch]);

    // Wait until AppInitializer (or LoginPage) signals init is complete.
    // No protected route may run before storage / Redux are populated —
    // otherwise pages that read storage in onInit hit "Storage not initialized".
    if (isLoading || isInitializing) {
        return <LogoTextBrandFontLoadingIndicator />;
    }

    if (!isAuthenticated && !isPublicPath) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
};

// Public Route component (redirect to home if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    const { isAuthenticated, isLoading, redirectTo } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const [destination, setDestination] = useState<string | null>(null);

    // Handle redirect when user becomes authenticated
    useEffect(() => {
        if (isAuthenticated && !isLoading && !destination) {
            // Only set destination once (when it's null)
            const target = redirectTo || '/';
            setDestination(target);

            // Clear the redirect after a short delay
            setTimeout(() => {
                if (redirectTo) {
                    dispatch(clearRedirectTo());
                }
            }, 100);
        }
    }, [isAuthenticated, isLoading, redirectTo, destination, dispatch]);

    if (isLoading) {
        return <LogoTextBrandFontLoadingIndicator />;
    }

    if (isAuthenticated && destination) {
        return <Navigate to={destination} replace />;
    }

    return <>{children}</>;
};

// App Routes component (needs to be inside Provider to access Redux)
export const AppRoutes = () => {
    return (
        <LocaleProvider>
            <ThemeProvider>
                <VideoPlayerProvider>
                    <IOSCompatibilityCheck>
                        <Router>
                            <KeepAliveProvider
                                maxSize={15}
                                enableAdaptivePerformance={true}
                            >
                                <ExpauseSheetProvider>
                                    <RootScreen>
                                        {/* Remote Config first: Flutter fetches it before the
                                          * login flow (`root_screen.dart:163-170`), so start the
                                          * fetch as early as possible. Ordering is a start-order
                                          * preference, not a correctness dependency — every
                                          * getter falls back to its compile-time default until
                                          * the first activate() lands. Not auth-gated: the
                                          * budget-exceeded overlay must reach the login screen. */}
                                        <RemoteConfigListener />
                                        {/* AppInitializer handles initialization on app restart */}
                                        <AppInitializer />
                                        <AuthUserListener />
                                        <PremiumPackagesListener />
                                        <ExclusiveContentListener />
                                        <WalletListener />
                                        <PaymentExchangeRateLoader />
                                        <UserDeviceListener />
                                        <LastSeenTimestampListener />
                                        <AppToaster />
                                        <VideoUploadProgressOverlay />
                                        {/* App-level blocking overlays (budget exceeded /
                                          * account suspended). Mounted here — inside RootScreen,
                                          * outside <Routes> — so they survive route changes, the
                                          * web equivalent of Flutter's root `Overlay` insert
                                          * (`root_screen.dart:306-320`). */}
                                        <AppOverlayHost />
                                        <Routes>
                                            {/* Login page — kept outside the protected subtree because it has
                                              * its own full-bleed branding layout and never needs the
                                              * `KeepAliveOutlet` cache. */}
                                            <Route
                                                path="/login"
                                                element={
                                                    <PublicRoute>
                                                        <LoginPage />
                                                    </PublicRoute>
                                                }
                                            />

                                            {/* All other routes (public + protected) share the same
                                              * `ProtectedRoutesWrapper`, so they all live under the same
                                              * `KeepAliveOutlet`. `ProtectedRoute` short-circuits the
                                              * login redirect for `PUBLIC_ROUTES` paths — that keeps the
                                              * legal pages reachable without authentication while still
                                              * letting them participate in the route cache (so navigating
                                              * back from a legal page restores the underlying cached
                                              * page rather than force-remounting it). */}
                                            <Route
                                                path="/"
                                                element={
                                                    <ProtectedRoute>
                                                        <ProtectedRoutesWrapper />
                                                    </ProtectedRoute>
                                                }
                                            >
                                                {/* Public routes without TopBar (e.g., legal documents) */}
                                                <Route element={<PublicRouteContainer />}>
                                                    {PUBLIC_ROUTES.map((routeConfig, index) => {
                                                        const Element = routeConfig.element;
                                                        return (
                                                            <Route
                                                                key={`public-${index}`}
                                                                path={routeConfig.path}
                                                                element={<Element />}
                                                            />
                                                        );
                                                    })}
                                                </Route>

                                                {/* Main navigation routes with TopBar */}
                                                <Route element={<MainRouteContainer />}>
                                                    {MAIN_NAVIGATION_ROUTES.map((routeConfig, index) => {
                                                        const Element = routeConfig.element;

                                                        // Handle index route (path="/")
                                                        if (routeConfig.path === '/' || routeConfig.path === '') {
                                                            return <Route key={index} index element={<Element />} />;
                                                        }

                                                        return (
                                                            <Route
                                                                key={index}
                                                                path={routeConfig.path}
                                                                element={<Element />}
                                                            />
                                                        );
                                                    })}

                                                    {/* Commented out to avoid duplicate route instances */}
                                                    {/* Redirect /feed to / (same component, avoid duplicate instances) */}
                                                    {/* <Route path="feed" element={<Navigate to="/" replace />} /> */}
                                                </Route>

                                                {/* Detail routes without TopBar */}
                                                <Route element={<DetailsRouteContainer />}>
                                                    {DETAIL_ROUTES.map((routeConfig, index) => {
                                                        const Element = routeConfig.element;

                                                        return (
                                                            <Route
                                                                key={`detail-${index}`}
                                                                path={routeConfig.path}
                                                                element={<Element />}
                                                            />
                                                        );
                                                    })}
                                                </Route>
                                            </Route>

                                            {/* Catch all route */}
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Routes>
                                    </RootScreen>
                                </ExpauseSheetProvider>
                            </KeepAliveProvider>
                        </Router>
                    </IOSCompatibilityCheck>
                </VideoPlayerProvider>
            </ThemeProvider>
        </LocaleProvider>
    );
};